import React, { useState, useEffect, useCallback } from 'react'
import { Modal, Form, Input, Row, Col, Button, Spin } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import MerchantList from '../../../components/MerchantList';
import CatchError from '../../../helper/CatchError';
import { API_MERCHANT } from '../../../config/config';
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

const AddMerchantModal = (props) => {

    const { t, isAddMerchantModalVisible, setIsAddMerchantModalVisible, reloadPage } = props;
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [merchantList, setMerchantList] = useState([]);
    const [selectedMerchant, setSelectedMerchant] = useState(null);

    const fetchMerchantList = useCallback(async () => {
        try {
            const url = API_MERCHANT + "/list";
            const response = await axios.post(url, { getall: true }, {
                headers: { Authorization: "Bearer " + Cookies.get("access_token") }
            });
            const list = response.data?.list;
            if (list && Array.isArray(list)) {
                setMerchantList(list);
            } else {
                throw new Error("Unexpected response format from merchant list API");
            }
        } catch (error) {
            console.error("Error fetching merchant list:", error);
            Swal.fire({
                icon: "error",
                title: "Failed to Load Merchants",
                text: "Unable to fetch merchant list. Please refresh the page and try again.",
                showConfirmButton: true
            });
        }
    }, []);

    // Fetch merchant list on mount
    useEffect(() => {
        const loadInitialData = async () => {
            setIsInitialLoading(true);
            await fetchMerchantList();
            setIsInitialLoading(false);
        };
        loadInitialData();
    }, [fetchMerchantList]);

    // Auto-select merchant if only one available
    useEffect(() => {
        if (merchantList.length === 1 && !selectedMerchant) {
            const singleMid = merchantList[0].mid;
            form.setFieldsValue({ mid: singleMid });
            setSelectedMerchant(singleMid);
        }
    }, [merchantList, selectedMerchant, form]);

    const handleMerchantChange = useCallback((value) => {
        setSelectedMerchant(value);
    }, []);

    const handleSubmit = () => {
        setIsLoading(true);
        form.validateFields().then((values) => {
            const payload = {
                mid: values.mid,
                retailer_name: values.retailer_name,
                site_name: `${values.retailer_name}`,
                email_for_alert_notification: (values.alert_notification_emails || [])
                .map((item) => item?.email)
                .filter(Boolean)
                .join(','),
            };

            const url = API_MERCHANT + "/retailer-site-enrollment";
            axios
                .post(url, payload, { headers: { Authorization: "Bearer " + Cookies.get("access_token") } })
                .then(({ data }) => {
                    if (data.status === 'ok') {
                        Swal.fire({
                            icon: "success",
                            title: t('contents.merchants.addMerchant.success_message', { retailer_name: values.retailer_name }),
                            showConfirmButton: false,
                            timer: 1500,
                            onClose: () => {
                                form.resetFields();
                                setSelectedMerchant(null);
                                setIsAddMerchantModalVisible(false);
                                reloadPage();
                            }
                        });
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: t("login.dialogs.catch"),
                            text: data.msg ? data.msg : "",
                            showConfirmButton: true
                        });
                    }
                })
                .catch((error) => {
                    Swal.fire({
                        icon: "error",
                        title: t("login.dialogs.catch"),
                        text: error.response?.data?.error_msg?.msg || error.response?.data?.msg || "An error occurred",
                        showConfirmButton: true,
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                })
        }).catch(() => {
            setIsLoading(false);
            CatchError('Unable to create merchant.');
        });
    }

    const handleCancel = () => {
        form.resetFields();
        setSelectedMerchant(null);
        setIsAddMerchantModalVisible(false);
    };

    return <Modal
        forceRender={true}
        title={t("contents.merchants.addMerchant.title")}
        width="800"
        style={{
            maxWidth: "800px"
        }}
        visible={isAddMerchantModalVisible}
        onOk={handleSubmit}
        okText={"Submit"}
        onCancel={handleCancel}
        footer={[
            <Button key="back" onClick={handleCancel}>
                {t("contents.merchants.form.cancel")}
            </Button>,
            <Button
                key="submit"
                type="primary"
                onClick={handleSubmit}
                htmlType="submit"
                loading={isLoading}
                disabled={isInitialLoading}
            >
                {t("contents.merchants.form.submit")}
            </Button>
        ]}
    >
        <Spin spinning={isInitialLoading} tip="Loading...">
            <Form layout="vertical" form={form}>
                <Row gutter={[16, 16]}>

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                        <Form.Item
                            name="mid"
                            label={t('contents.merchants.addMerchant.form.merchant')}
                            rules={[{ required: true, message: t('required_field') }]}
                        >
                            <MerchantList
                                placeholder={t("contents.merchants.title")}
                                mode={null}
                                showInactive={false}
                                onChange={handleMerchantChange}
                                disabled={isInitialLoading}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                        <Form.Item
                            name="retailer_name"
                            label={t('contents.merchants.addMerchant.form.retailer')}
                            rules={[{ required: true, message: t('required_field') }]}
                        >
                            <Input placeholder="Enter retailer name" disabled={isInitialLoading} />
                        </Form.Item>
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                        <Form.Item
                            label={t('contents.merchants.addMerchant.form.email_for_alert_notification')}
                        >
                            <Form.List name="alert_notification_emails">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field) => (
                                            <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Form.Item
                                                    name={[field.name, 'email']}
                                                    rules={[{
                                                        type: 'email',
                                                        message: t('contents.merchants.addMerchant.form.valid_email') || 'Please enter a valid email'
                                                    }]}
                                                    style={{ flex: 1, marginBottom: 0 }}
                                                >
                                                    <Input
                                                        placeholder="email@example.com"
                                                        disabled={isInitialLoading}
                                                    />
                                                </Form.Item>
                                                <Button
                                                    danger
                                                    icon={<MinusCircleOutlined />}
                                                    onClick={() => remove(field.name)}
                                                    disabled={isInitialLoading}
                                                />
                                            </div>
                                        ))}
                                        <Form.Item style={{ marginBottom: 0 }}>
                                            <Button
                                                type="dashed"
                                                onClick={() => add()}
                                                icon={<PlusOutlined />}
                                                disabled={isInitialLoading}
                                                block
                                            >
                                                {t('contents.merchants.addMerchant.form.add_alert_notification_email') || 'Add Alert Notification Email'}
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form.List>
                        </Form.Item>
                    </Col>

                </Row>
            </Form>
        </Spin>
    </Modal>
}

export default AddMerchantModal;
